import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';

const CompanyContext = createContext();

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Default company state - used to reset state cleanly
const DEFAULT_COMPANY_STATE = {
  company_name: 'Regulisto',
  company_logo: null,
  company_cif: '',
  company_address: '',
  company_city: '',
  company_phone: '',
  company_email: '',
  page_title: 'Regulisto - Control Horario',
  favicon: null,
  timezone: 'Europe/Madrid'
};

// DEBUG: Disable in production
const DEBUG_MODE = false;
const debugLog = (...args) => {
  if (DEBUG_MODE) {
    console.log('[CompanyContext]', new Date().toISOString(), ...args);
  }
};

export const CompanyProvider = ({ children }) => {
  const [company, setCompany] = useState(DEFAULT_COMPANY_STATE);
  const [loading, setLoading] = useState(true);
  // New: Track when we're actively switching organizations
  const [isSwitchingOrganization, setIsSwitchingOrganization] = useState(false);
  const lastOrgIdRef = useRef(null);
  // Track in-flight requests to prevent race conditions
  const fetchRequestIdRef = useRef(0);

  const updateBrowserMeta = (settings) => {
    // Update page title
    if (settings.page_title) {
      document.title = settings.page_title;
    }
    
    // Update favicon
    if (settings.favicon) {
      let link = document.querySelector("link[rel*='icon']");
      if (!link) {
        link = document.createElement('link');
        link.rel = 'icon';
        document.head.appendChild(link);
      }
      link.href = settings.favicon;
    }
  };

  const fetchCompanySettings = useCallback(async (forceRefresh = false, reason = 'unknown') => {
    // Generate unique request ID to track this specific request
    const requestId = ++fetchRequestIdRef.current;
    debugLog(`fetchCompanySettings START - requestId: ${requestId}, reason: ${reason}, forceRefresh: ${forceRefresh}`);
    
    try {
      const token = localStorage.getItem('token');
      const currentOrgStr = localStorage.getItem('currentOrganization');
      let currentOrgId = null;
      let currentOrgName = null;
      
      try {
        const currentOrg = JSON.parse(currentOrgStr);
        currentOrgId = currentOrg?.id || null;
        currentOrgName = currentOrg?.name || null;
      } catch (e) {
        debugLog('Failed to parse currentOrganization from localStorage');
      }
      
      debugLog(`Current state - lastOrgId: ${lastOrgIdRef.current}, newOrgId: ${currentOrgId}, orgName: ${currentOrgName}`);
      
      // Skip fetch if org hasn't changed and not forced
      if (!forceRefresh && lastOrgIdRef.current === currentOrgId && !loading) {
        debugLog(`SKIP fetch - no change detected`);
        return;
      }
      
      // If org changed, set switching state and reset
      const orgChanged = lastOrgIdRef.current !== currentOrgId;
      if (orgChanged) {
        debugLog(`ORG CHANGED from ${lastOrgIdRef.current} to ${currentOrgId} - resetting state`);
        setIsSwitchingOrganization(true);
        setCompany(DEFAULT_COMPANY_STATE);
        lastOrgIdRef.current = currentOrgId;
      }
      
      if (token) {
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        
        debugLog(`Fetching settings from API...`);
        const response = await axios.get(`${API_URL}/settings`);
        const settingsData = response.data;
        
        // Check if this request is still valid (no newer request started)
        if (requestId !== fetchRequestIdRef.current) {
          debugLog(`STALE REQUEST ${requestId} - newer request ${fetchRequestIdRef.current} exists, ignoring response`);
          return;
        }
        
        debugLog(`API Response received:`, {
          company_name: settingsData.company_name,
          company_logo: settingsData.company_logo ? 'present' : 'null',
          requestId
        });
        
        // Replace state ATOMICALLY - construct the full object first
        const newCompanyState = { ...DEFAULT_COMPANY_STATE, ...settingsData };
        debugLog(`Setting new company state:`, {
          company_name: newCompanyState.company_name,
          company_logo: newCompanyState.company_logo ? 'present' : 'null'
        });
        
        // Single setState call to prevent partial updates
        setCompany(newCompanyState);
        updateBrowserMeta(settingsData);
      } else {
        // No token - reset to defaults and try public settings
        debugLog(`No token - resetting to defaults`);
        setCompany(DEFAULT_COMPANY_STATE);
        lastOrgIdRef.current = null;
        
        try {
          const publicResponse = await axios.get(`${API_URL}/settings/public`);
          if (requestId === fetchRequestIdRef.current) {
            setCompany({ ...DEFAULT_COMPANY_STATE, ...publicResponse.data });
            updateBrowserMeta(publicResponse.data);
          }
        } catch (e) {
          // Ignore errors for public settings
        }
      }
    } catch (error) {
      debugLog(`ERROR fetching company settings:`, error.message);
      // On error, only reset if this is still the current request
      if (requestId === fetchRequestIdRef.current) {
        setCompany(DEFAULT_COMPANY_STATE);
      }
    } finally {
      // Only update loading states if this is still the current request
      if (requestId === fetchRequestIdRef.current) {
        debugLog(`fetchCompanySettings END - requestId: ${requestId}`);
        setLoading(false);
        setIsSwitchingOrganization(false);
      }
    }
  }, [loading]);

  // Reset state completely on logout
  const resetCompany = useCallback(() => {
    debugLog('resetCompany called - clearing all state');
    fetchRequestIdRef.current++; // Invalidate any pending requests
    setCompany(DEFAULT_COMPANY_STATE);
    lastOrgIdRef.current = null;
    setIsSwitchingOrganization(false);
    document.title = DEFAULT_COMPANY_STATE.page_title;
  }, []);

  useEffect(() => {
    debugLog('Initial mount - fetching settings');
    fetchCompanySettings(true, 'initial_mount');
    
    // Listen for storage changes (login/logout/org change)
    const handleStorageChange = (e) => {
      debugLog(`Storage event received: key=${e.key}, newValue=${e.newValue ? 'present' : 'null'}`);
      if (e.key === 'token') {
        if (!e.newValue) {
          // Token removed = logout
          debugLog('Token removed - triggering reset');
          resetCompany();
        } else {
          // New token = login
          debugLog('New token detected - fetching settings');
          fetchCompanySettings(true, 'token_changed');
        }
      } else if (e.key === 'currentOrganization') {
        // Organization changed - this is the critical path
        debugLog('Organization changed in storage - fetching new settings');
        fetchCompanySettings(true, 'org_changed_storage_event');
      }
    };
    window.addEventListener('storage', handleStorageChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [fetchCompanySettings, resetCompany]);

  const refreshCompany = useCallback(() => {
    debugLog('refreshCompany called explicitly');
    fetchCompanySettings(true, 'explicit_refresh');
  }, [fetchCompanySettings]);

  return (
    <CompanyContext.Provider value={{ 
      company, 
      loading, 
      isSwitchingOrganization,
      refreshCompany, 
      resetCompany 
    }}>
      {children}
    </CompanyContext.Provider>
  );
};

export const useCompany = () => {
  const context = useContext(CompanyContext);
  if (!context) {
    throw new Error('useCompany must be used within a CompanyProvider');
  }
  return context;
};

export default CompanyContext;
